import type { ApiError as ErrorBody } from "@gtm/contracts/http";
import createClient from "openapi-fetch";
import type { Paths } from "./api-paths";

export { ApiError };

/**
 * The workspace-scoped API client.
 *
 * Same shape as the generated SDK in the existing GTM application, so feature
 * code reads identically: `unwrap(apiClient().GET("/people", { params: … }))`.
 * The base URL carries the workspace, so no call site has to remember it.
 */
class ApiError extends Error {
  constructor(
    public status: number,
    public body: ErrorBody,
  ) {
    super(body.error);
    this.name = "ApiError";
  }
}

export function apiClient() {
  const workspaceId = /^\/w\/([^/]+)/.exec(window.location.pathname)?.[1];
  if (!workspaceId) throw new Error("Choose a workspace first.");
  return createClient<Paths>({
    baseUrl: `/api/workspaces/${encodeURIComponent(workspaceId)}`,
    credentials: "include",
  });
}

/** The unauthenticated surface: session and sign-out. */
export function authClient() {
  return createClient<Record<string, Record<string, unknown>>>({
    baseUrl: "/api/auth",
    credentials: "include",
  });
}

export async function unwrap<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const result = await request;
  if (result.response.status === 401) window.dispatchEvent(new Event("session-expired"));
  if (result.error || !result.response.ok) {
    throw new ApiError(
      result.response.status,
      (result.error as ErrorBody) || { error: "The request could not be completed." },
    );
  }
  // A 204 has no body by design, so "no data" is the correct answer rather than
  // a fault. Treating it as one meant two dozen endpoints — every save, update
  // and delete that answers 204 — reported failure for work they had just done,
  // which is worse than a silent success: it invites somebody to do it again.
  if (result.response.status === 204 || result.response.status === 205) {
    return undefined as T;
  }
  if (result.data === undefined) {
    throw new ApiError(502, { error: "The API returned an empty response." });
  }
  return result.data;
}
